import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { NextPageContext } from 'next';
import {
  AdDetails,
  AdOrder,
  GetAdDetailsResponse,
  GetAdsResponse,
  NewAdDefaultValues,
  ServiceName,
} from '../types/ads.types';
import {
  GET_AD_DETAILS,
  GET_AD_DETAILS_FOR_EDIT,
  GET_ADS,
} from '../queries/ads.queries';
import { apolloClient } from '../apolloClient';
import { PlatformSource } from '../types/common.types';
import { GetRegionsResponse } from '@/api/types/regions.types';
import {
  CategoryBaseInfo,
  CategoryOrderEnum,
  CategoryScopeEnum,
  GetCategoriesResponse,
  GetCategoryPropertiesResponse,
  GetRootCategoriesResponse,
} from '@/api/types/categories.types';
import {
  GET_CATEGORIES,
  GET_CATEGORY_PROPERTIES,
  GET_ROOT_CATEGORIES,
} from '@/api/queries/categories.queries';
import { GET_REGIONS } from '@/api/queries/regions.queries';
import { findCategory } from '@/components/AdForm/components/CategorySelect/components/CategorySelectBox.helpers';
import { QUERY, ROOT_CATEGORY_SLUG } from '@/constants/common';
import {
  EDIT_AD_PAGE_SLUG,
  EDIT_AD_PAGE_SLUG_INDEX,
  LEGACY_ID_SLUG_INDEX,
  NEW_AD_PAGE_SLUG,
} from '@/hooks/usePageType/usePageType.constants';
import { AdStatus } from '@/types';
import { EditPopupType } from '@/components/Ad/Settings/Settings.types';
import { getPlatformSource } from '@/utils/getPlatformSource';
import { DeviceType } from '@/utils/getDeviceType';
import { parseQuery } from '@/hooks/useParseQuery/useParseQuery.helpers';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { getCurrentUrlFromContext, getFulfilledValue } from '@/utils/helpers';

async function getLoggingHeaders(
  context: NextPageContext,
  source: PlatformSource
) {
  const userAgent =
    (context.req
      ? context.req?.headers['user-agent']
      : window.navigator.userAgent) || '';

  const ipAddr =
    context.req?.headers['cf-connecting-ip'] ||
    context.req?.headers['x-forwarded-for'] ||
    '';
  const countryCode =
    context.req?.headers['cf-ipcountry'] ||
    context.req?.headers['x-vercel-ip-country'] ||
    '';

  const { signRequest } = await import('@/utils/signRequest');
  const { signature, timestamp } = signRequest(
    ipAddr as string,
    countryCode as string
  );

  return {
    ...(countryCode ? { 'X-Vercel-Client-IPCountry': countryCode } : {}),
    ...(timestamp ? { 'X-Vercel-Timestamp': timestamp.toString() } : {}),
    ...(signature ? { 'X-Vercel-Signature': signature } : {}),
    ...(ipAddr ? { 'X-Vercel-Client-IP': ipAddr } : {}),
    ...(userAgent ? { 'User-Agent': userAgent } : {}),
    ...(source && { 'X-Platform': source }),
  };
}

export async function getServerAdDetails(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext,
  deviceType: DeviceType
): Promise<{
  adDetails: AdDetails | null;
}> {
  const isBrowser = typeof window !== 'undefined';
  const isEditPage =
    context.query.slug?.[EDIT_AD_PAGE_SLUG_INDEX] === EDIT_AD_PAGE_SLUG;

  if (isBrowser && !isEditPage) {
    return { adDetails: null };
  }

  const legacyId = context.query.slug?.[LEGACY_ID_SLUG_INDEX] as string;
  const userAgent =
    (context.req
      ? context.req?.headers['user-agent']
      : window.navigator.userAgent) || '';
  const isSSRWebView = userAgent.indexOf('miniApp') >= 0;
  const platformSource = getPlatformSource(deviceType);

  const hasAccessToken = !!context?.query?.access_token;

  if (
    // If user just logged in, we're not ready to fetch ad details yet
    // Because we have to wait for the user to be authenticated, so we skip the query and make this query on client side when access token is present and set to cookies
    hasAccessToken ||
    !legacyId ||
    legacyId === NEW_AD_PAGE_SLUG
  ) {
    return { adDetails: null };
  }

  const source = isSSRWebView ? PlatformSource.App : platformSource;

  const adDetails = await apolloClient<GetAdDetailsResponse>(
    client,
    {
      query: isEditPage ? GET_AD_DETAILS_FOR_EDIT : GET_AD_DETAILS,
      variables: {
        legacyId,
        source,
      },
      errorPolicy: 'all',
      context: {
        headers: isEditPage ? {} : await getLoggingHeaders(context, source),
      },
    },
    context
  );

  const adStatus = adDetails?.data?.adDetails?.status;
  const editLockedUntil = adDetails?.data?.adDetails?.editLockedUntil;

  if (
    isEditPage &&
    (!adStatus || adStatus !== AdStatus.Published || editLockedUntil)
  ) {
    const currentUrl = isBrowser ? window.location.href : context.asPath;
    const adUrl =
      currentUrl?.replace(
        `/${EDIT_AD_PAGE_SLUG}`,
        editLockedUntil
          ? `?set_edit_limit_reached=true#${EditPopupType.Error}` // TODO: remove ?set_edit_limit_reached=true after implementing responsive ad page
          : ''
      ) ?? '';

    if (!isBrowser && context.res) {
      context.res.writeHead(302, { location: adUrl }).end();
    } else {
      window.location.replace(adUrl);
    }
  }

  return {
    adDetails: adDetails?.data?.adDetails || null,
  };
}

export async function getNewAdDefaultValues(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext
): Promise<NewAdDefaultValues | null> {
  const asPath = context.asPath?.split('?')[0].slice(1) || '';

  if (!asPath.endsWith(NEW_AD_PAGE_SLUG)) return null;

  const categoriesData = await apolloClient<GetCategoriesResponse>(
    client,
    {
      query: GET_CATEGORIES,
      variables: {
        scope: CategoryScopeEnum.CREATE_AD,
        orderType: CategoryOrderEnum.POSITION_ASC,
        hasBilingualSearch: true,
      },
      fetchPolicy: 'no-cache',
    },
    context
  );
  const regionsData = await apolloClient<GetRegionsResponse>(
    client,
    { query: GET_REGIONS },
    context
  );
  const categories = categoriesData?.data?.categories || [];
  const regions = regionsData?.data?.regions || [];
  const defaultRegion = regions?.find(region => region.default);

  const category = findCategory(categories, asPath.replace('/new', ''));

  const isSubcategory = category?.parent?.path !== ROOT_CATEGORY_SLUG;
  const defaultCategory = category && !isSubcategory ? category : null;
  const defaultSubcategory = category && isSubcategory ? category : null;

  return {
    categories,
    defaultRegion,
    defaultCategory,
    defaultSubcategory,
  };
}

export async function getSearchResultsServerData(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext,
  currentCategory: CategoryBaseInfo | null,
  source: PlatformSource
) {
  const currentUrl = getCurrentUrlFromContext(context);

  const regions = await apolloClient<GetRegionsResponse>(
    client,
    {
      query: GET_REGIONS,
    },
    context
  );

  if (!regions) {
    return null;
  }

  const loggingHeaders = await getLoggingHeaders(context, source);

  const {
    keywords,
    categoryId: categoryIdFromQuery,
    price,
    order,
    regionId,
    propertyOptions,
    userLegacyId,
  } = parseQuery(context.query, regions.data);

  const keywordsSource = context.query[QUERY.keywords_source] as string;
  const isUserAdsPage = !!userLegacyId;
  const currentCategoryId = categoryIdFromQuery || currentCategory?.id;
  const isAllCategories = currentCategory?.path === ROOT_CATEGORY_SLUG;

  const filters = {
    categoryId: isAllCategories ? null : currentCategoryId,
    price,
    regionId,
    propertyOptions,
    userLegacyId,
  };

  const promises = [];

  promises.push(
    apolloClient<GetRootCategoriesResponse>(
      client,
      {
        query: GET_ROOT_CATEGORIES,
        variables: {
          keywords,
          filters,
        },
      },
      context
    )
  );

  if (isUserAdsPage) {
    promises.push(
      apolloClient<GetAdsResponse>(
        client,
        {
          query: GET_ADS(ServiceName.Latest),
          variables: {
            first: AdsChunkSize.Large,
            filters: {
              categoryId: isAllCategories ? null : currentCategory?.id,
              userLegacyId,
              propertyOptions,
            },
            source,
          },
          context: { headers: loggingHeaders },
        },
        context
      )
    );
  } else {
    promises.push(
      apolloClient<GetAdsResponse>(
        client,
        {
          query: GET_ADS(ServiceName.Vip),
          variables: {
            orderType: AdOrder.Random,
            adKind: ServiceName.Vip,
            first: AdsChunkSize.Small,
            keywords,
            filters,
            source,
          },
        },
        context
      )
    );

    promises.push(
      apolloClient<GetAdsResponse>(
        client,
        {
          query: GET_ADS(ServiceName.Latest),
          variables: {
            orderType: order,
            first: AdsChunkSize.Large,
            keywords,
            filters,
            keywordsSource: keywordsSource?.toUpperCase(),
            sourceLink: currentUrl.replace(/%20/g, '+'),
            source,
          },
          context: { headers: loggingHeaders },
        },
        context
      )
    );

    promises.push(
      apolloClient<GetCategoryPropertiesResponse>(
        client,
        {
          query: GET_CATEGORY_PROPERTIES,
          variables: {
            path: currentCategory?.path,
            collectionFilters: propertyOptions.collection,
          },
          errorPolicy: 'all',
        },
        context
      )
    );
  }

  if (isUserAdsPage) {
    const [rootCategories, userAds, categoryProperties] = (
      await Promise.allSettled(promises)
    ).map(getFulfilledValue);
    return {
      regions: regions ?? null,
      rootCategories: rootCategories ?? null,
      userAds: userAds ?? null,
      categoryProperties: categoryProperties ?? null,
    };
  }

  const [rootCategories, vipAds, latestAds, categoryProperties] = (
    await Promise.allSettled(promises)
  ).map(getFulfilledValue);
  return {
    regions: regions ?? null,
    rootCategories: rootCategories ?? null,
    vipAds: vipAds ?? null,
    latestAds: latestAds ?? null,
    categoryProperties: categoryProperties ?? null,
  };
}
